import React, { useEffect, useState } from "react";
import { Button, Checkbox, Input } from "../ui";
import LudDashboardModal from "./LudDashboardModal";
import { useSubmitGuard } from "./modalHooks";
import { integrationLabel } from "../../utils/integrations";
import { isValidLocationUrl, isValidPhone, normalizePhone } from "../../utils/branchValidation";

// Canonical keys; labels come from the registry.
const ALL_APPS = ["jahez", "hungerstation", "keeta", "toyou", "marsool", "thechefz", "ninja", "feedus"];

const EMPTY_FORM = {
  name: "",
  city: "",
  phone: "",
  locationUrl: "",
  canEditHours: false,
  canEditMenu: false,
  from1: "09:00",
  to1: "01:00",
  hasSecondShift: false,
  from2: "",
  to2: "",
  hoursNote: "",
  linkedApps: [],
  requestedApps: [],
};

const fromBranch = (branch) => ({
  ...EMPTY_FORM,
  ...branch,
  // The backend stores a single name/city; the Arabic value is the source.
  name: branch.nameAr || branch.nameEn || "",
  city: branch.cityAr || branch.cityEn || "",
});

// `draft`: a client setting up the store adds / edits a DRAFT branch (name, city, phone, location). Hours, applications and permissions
// are requested later, once the subscription is active, so they are not offered here.
export default function BranchFormModal({ open, lang, branch, draft = false, extra = null, onClose, onSave }) {
  // Repeat clicks are ignored while a save returned by the caller is running.
  const [saving, runSave] = useSubmitGuard();
  const [form, setForm] = useState(EMPTY_FORM);
  const [showErrors, setShowErrors] = useState(false);
  const ar = lang === "ar";

  useEffect(() => {
    if (!open) return;
    setForm(branch ? fromBranch(branch) : EMPTY_FORM);
    setShowErrors(false);
    // Initialise when the modal opens or the entity changes, not whenever the
    // parent passes a new object/array for the same data (that wiped edits).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, branch?.id]);

  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const toggleListValue = (key, value, checked) => {
    setForm((current) => ({
      ...current,
      [key]: checked
        ? [...new Set([...(current[key] || []), value])]
        : (current[key] || []).filter((item) => item !== value),
    }));
  };

  const errors = {
    name: !form.name.trim() ? (ar ? "اسم الفرع مطلوب" : "Branch name is required") : "",
    phone: !String(form.phone || "").trim()
      ? (ar ? "رقم التواصل مطلوب" : "Phone number is required")
      : !isValidPhone(form.phone)
        ? (ar ? "رقم غير صحيح (7–20 رقماً، يمكن أن يبدأ بـ +)" : "Invalid number (7–20 digits, may start with +)")
        : "",
    locationUrl:
      String(form.locationUrl || "").trim() && !isValidLocationUrl(form.locationUrl)
        ? (ar ? "الرابط غير صحيح (يجب أن يبدأ بـ https://)" : "Invalid link (must start with https://)")
        : "",
    hours:
      !branch && form.hasSecondShift && (!form.from2 || !form.to2)
        ? (ar ? "أكمل وقتي الفترة الثانية" : "Fill in both second-shift times")
        : "",
  };
  const invalid = Object.values(errors).some(Boolean);
  const shown = (key) => (showErrors && errors[key] ? errors[key] : undefined);

  const submit = () => {
    if (invalid) {
      setShowErrors(true);
      return undefined;
    }
    const name = form.name.trim();
    const city = form.city.trim();
    return runSave(onSave, {
      ...form,
      nameAr: name,
      nameEn: name,
      cityAr: city,
      cityEn: city,
      phone: normalizePhone(form.phone),
      locationUrl: String(form.locationUrl || "").trim(),
    });
  };

  return (
    <LudDashboardModal
      open={open}
      busy={saving}
      size="lg"
      title={branch ? (ar ? "تعديل بيانات الفرع" : "Edit Branch Data") : draft ? (ar ? "إضافة فرع (مسودة)" : "Add Branch (Draft)") : (ar ? "طلب إضافة فرع جديد" : "Request New Branch")}
      onClose={onClose}
      footer={
        <>
          <Button variant="primary" appearance="filled" onClick={submit} disabled={saving} loading={saving}>
            {branch || draft ? (ar ? "حفظ" : "Save") : (ar ? "إرسال الطلب" : "Send Request")}
          </Button>
          <Button variant="danger" appearance="soft" onClick={onClose} disabled={saving}>
            {ar ? "إلغاء" : "Cancel"}
          </Button>
        </>
      }
    >
      <div className="lud-form-grid">
        <Input
          label={ar ? "اسم الفرع" : "Branch Name"}
          value={form.name}
          error={shown("name")}
          onChange={(event) => update("name", event.target.value)}
        />
        <Input label={ar ? "المدينة" : "City"} value={form.city} onChange={(event) => update("city", event.target.value)} />
        <Input
          label={ar ? "رقم التواصل" : "Phone"}
          type="tel"
          inputMode="tel"
          dir="ltr"
          value={form.phone}
          error={shown("phone")}
          onChange={(event) => update("phone", event.target.value)}
        />
        <div className="lud-branch-url-field">
          <Input
            label={ar ? "رابط موقع الفرع" : "Location URL"}
            type="url"
            dir="ltr"
            placeholder="https://maps.google.com/..."
            value={form.locationUrl}
            error={shown("locationUrl")}
            onChange={(event) => update("locationUrl", event.target.value)}
          />
          {form.locationUrl && isValidLocationUrl(form.locationUrl) && (
            <a href={form.locationUrl.trim()} target="_blank" rel="noreferrer noopener">
              {ar ? "فتح الموقع للتحقق ↗" : "Open to check ↗"}
            </a>
          )}
        </div>
      </div>

      {draft && (
        <p>{ar ? "أضف بيانات الفرع الأساسية. تفعيل الفرع وأوقات العمل والتطبيقات تتوفر بعد تفعيل الاشتراك." : "Add the branch's basic details. Activating the branch, working hours and applications are available once your subscription is active."}</p>
      )}

      {!branch && !draft && (
        <>
          <div className="lud-branch-modal-section">
            <strong>{ar ? "أوقات العمل" : "Working Hours"}</strong>
            <div className="lud-form-grid">
              <Input type="time" label={ar ? "من" : "From"} value={form.from1} onChange={(event) => update("from1", event.target.value)} />
              <Input type="time" label={ar ? "إلى" : "To"} value={form.to1} onChange={(event) => update("to1", event.target.value)} />
            </div>
            <Checkbox checked={form.hasSecondShift} onChange={(event) => update("hasSecondShift", event.target.checked)} label={ar ? "فيه فترة عمل ثانية في نفس اليوم" : "There is a second shift"} />
            {form.hasSecondShift && (
              <div className="lud-form-grid">
                <Input type="time" label={ar ? "من — الفترة الثانية" : "Second shift from"} value={form.from2} error={showErrors && !form.from2} onChange={(event) => update("from2", event.target.value)} />
                <Input type="time" label={ar ? "إلى — الفترة الثانية" : "Second shift to"} value={form.to2} error={showErrors && !form.to2} onChange={(event) => update("to2", event.target.value)} />
              </div>
            )}
            {shown("hours") && <p className="lud-branch-field-error" role="alert">{errors.hours}</p>}
            <Input label={ar ? "ملاحظات على الأوقات (اختياري — لو فيه أيام تختلف)" : "Hours note (optional)"} value={form.hoursNote} onChange={(event) => update("hoursNote", event.target.value)} />
            <p>{ar ? "يمكنك تعديل كل يوم على حدة بعد إضافة الفرع، ورفع رخصة الفرع من صفحة تفاصيله." : "You can edit each day separately and upload the branch license from its details after it is added."}</p>
          </div>

          <div className="lud-branch-modal-section">
            <strong>{ar ? "التطبيقات المرتبطة حاليًا" : "Currently Linked Applications"}</strong>
            <p>{ar ? "اختر أي تطبيق الفرع مرتبط فيه بالفعل." : "Select applications already linked to this branch."}</p>
            <div className="lud-branch-checkbox-grid">
              {ALL_APPS.map((app) => <Checkbox key={app} checked={form.linkedApps.includes(app)} onChange={(event) => toggleListValue("linkedApps", app, event.target.checked)} label={integrationLabel(app, lang)} />)}
            </div>
          </div>

          <div className="lud-branch-modal-section">
            <strong>{ar ? "التطبيقات الجديدة (طلب اشتراك)" : "New Applications (Subscription Request)"}</strong>
            <p>{ar ? "اختر أي تطبيق غير فعال حاليًا تبي تشترك فيه لهذا الفرع." : "Select new applications to request for this branch."}</p>
            <div className="lud-branch-checkbox-grid">
              {ALL_APPS.map((app) => <Checkbox key={app} checked={form.requestedApps.includes(app)} onChange={(event) => toggleListValue("requestedApps", app, event.target.checked)} label={integrationLabel(app, lang)} />)}
            </div>
          </div>
        </>
      )}

      {branch && extra}

      {!draft && (
      <div className="lud-branch-permissions-form">
        <strong>{ar ? "الصلاحيات المطلوبة" : "Requested Permissions"}</strong>
        <Checkbox checked={Boolean(form.canEditHours)} onChange={(event) => update("canEditHours", event.target.checked)} label={ar ? "صلاحية تعديل أوقات العمل" : "Can edit working hours"} />
        <Checkbox checked={Boolean(form.canEditMenu)} onChange={(event) => update("canEditMenu", event.target.checked)} label={ar ? "صلاحية تعديل قائمة الطعام للفرع" : "Can edit branch menu"} />
      </div>
      )}
    </LudDashboardModal>
  );
}
